import {
  calculateMarketFee,
  calculateSellerProceeds,
  DEFAULT_PAGE_SIZE,
  DEFAULT_TRADE_LOCK_DURATION_MS,
  type CancelListingResult,
  type CreateListingRequest,
  type MarketListing,
  type MarketSearchFilters,
  type MarketSearchResponse,
  type PurchaseResult,
  type SkinMarketStats,
} from '../cosmetics/marketplace-types';
import { STICKER_MANIFEST, type StickerInstance } from '../cosmetics/sticker-types';
import { weaponSkinRegistry } from '../cosmetics/weapon-skin-registry';
import type { WeaponSkinLoader } from '../cosmetics/weapon-skin-loader';
import { RARITY_COLORS, type SkinRarity, type WearLevel, wearLevelDisplayName, wearLevelFromFloat } from '../cosmetics/weapon-skin-types';
import type { SkinPreviewRenderer } from './skin-preview';
import { getToken } from '../auth/token-store';
import { isMobile } from '../core/device';

// U-H-001: /api/market/{list,buy,cancel} are gated by requireAuth on the
// server — every call must carry the JWT, same pattern as ClanPanel.authHeaders.
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

// Wear-level breakpoints used by the float bar gradient and tick marks.
// Mirror wearLevelFromFloat thresholds so the visual matches the textual
// label CS:GO players expect.
const WEAR_LEVEL_BREAKPOINTS: ReadonlyArray<{ label: string; max: number; color: string }> = [
  { label: 'FN', max: 0.07, color: '#3aa55d' },
  { label: 'MW', max: 0.15, color: '#7bc35a' },
  { label: 'FT', max: 0.38, color: '#d8c861' },
  { label: 'WW', max: 0.45, color: '#d68d3f' },
  { label: 'BS', max: 1.00, color: '#c5443d' },
];

const DEFAULT_SORT: NonNullable<MarketSearchFilters['sortBy']> = 'listed-desc';
const DEFAULT_LIMIT = 12;
const FALLBACK_SELLERS = ['Frontline Finds', 'Dust Trader', 'Midnight Crate', 'Eco Round', 'Bombsite Bazaar', 'Clutch Supply'];
const FALLBACK_SOURCE_MESSAGE = 'Live market API unavailable. Showing local showcase listings.';

function buildListingStickers(skinId: number): (StickerInstance | null)[] {
  const stickerIds = Array.from(STICKER_MANIFEST.keys());
  if (stickerIds.length === 0) return [];

  const count = skinId % 3;
  const stickers: (StickerInstance | null)[] = [];
  for (let slot = 0; slot < count; slot++) {
    const stickerId = stickerIds[(hashSeed(skinId * (slot + 7)) + slot) % stickerIds.length];
    stickers.push({
      stickerId,
      slot,
      wear: Number(seededNumber(skinId * (slot + 13), 0, 0.28).toFixed(2)),
      offsetX: Number(seededNumber(skinId * (slot + 17), -0.2, 0.2).toFixed(2)),
      offsetY: Number(seededNumber(skinId * (slot + 19), -0.2, 0.2).toFixed(2)),
      rotation: seededInteger(skinId * (slot + 23), -12, 12),
    });
  }
  return stickers;
}

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    #skin-market {
      position: fixed;
      inset: 0;
      z-index: 520;
      display: none;
      background:
        radial-gradient(circle at top, rgba(80, 120, 90, 0.12), transparent 35%),
        rgba(0, 0, 0, 0.93);
      font-family: 'Courier New', monospace;
      color: #fff;
    }

    #skin-market .sm-panel {
      width: min(1160px, calc(100vw - 48px));
      max-height: calc(100vh - 48px);
      margin: 24px auto;
      padding: 24px;
      overflow: auto;
      background: rgba(18, 22, 18, 0.96);
      border: 1px solid rgba(120, 150, 110, 0.35);
      border-radius: 6px;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
    }

    #skin-market .sm-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
      margin-bottom: 18px;
    }

    #skin-market .sm-title {
      margin: 0;
      color: #d8d19a;
      font-size: 22px;
      letter-spacing: 4px;
      text-transform: uppercase;
    }

    #skin-market .sm-subtitle {
      margin-top: 8px;
      color: #7c856f;
      font-size: 11px;
      letter-spacing: 1px;
      line-height: 1.5;
    }

    #skin-market .sm-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    #skin-market .sm-filter-toggle,
    #skin-market .sm-filter-backdrop {
      display: none;
    }

    #skin-market .sm-btn,
    #skin-market .sm-select,
    #skin-market .sm-input {
      font-family: 'Courier New', monospace;
    }

    #skin-market .sm-btn {
      padding: 10px 16px;
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.14);
      border-radius: 3px;
      color: #d2d2cb;
      cursor: pointer;
      letter-spacing: 1px;
      text-transform: uppercase;
      font-size: 11px;
    }

    #skin-market .sm-btn:hover:not(:disabled) {
      border-color: rgba(216, 209, 154, 0.7);
      color: #fff5c0;
    }

    #skin-market .sm-btn:disabled {
      opacity: 0.45;
      cursor: default;
    }

    #skin-market .sm-btn-primary {
      background: rgba(88, 122, 84, 0.28);
      border-color: rgba(128, 176, 120, 0.45);
      color: #dff2d6;
    }

    #skin-market .sm-filters {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
      margin-bottom: 18px;
    }

    #skin-market.sm-mobile-market .sm-filter-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    #skin-market.sm-mobile-market .sm-filter-backdrop {
      position: absolute;
      inset: 0;
      z-index: 529;
      background: rgba(0, 0, 0, 0.48);
    }

    #skin-market.sm-mobile-market.sm-filter-open .sm-filter-backdrop {
      display: block;
    }

    #skin-market.sm-mobile-market.sm-filter-open .sm-panel {
      overflow: visible;
    }

    #skin-market.sm-mobile-market .sm-filters {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 530;
      height: 70vh;
      display: none;
      grid-template-columns: 1fr;
      align-content: start;
      gap: 12px;
      margin: 0;
      padding: 18px;
      overflow-y: auto;
      background: rgba(18, 22, 18, 0.98);
      border-top: 1px solid rgba(216, 209, 154, 0.32);
      box-shadow: 0 -18px 42px rgba(0, 0, 0, 0.55);
    }

    #skin-market.sm-mobile-market.sm-filter-open .sm-filters {
      display: grid;
    }

    #skin-market .sm-sell {
      margin-bottom: 18px;
      padding: 14px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 5px;
      background: rgba(255, 255, 255, 0.025);
    }

    #skin-market .sm-sell-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
      align-items: end;
    }

    #skin-market .sm-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    #skin-market .sm-label {
      color: #838b7f;
      font-size: 11px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    #skin-market .sm-input,
    #skin-market .sm-select {
      width: 100%;
      padding: 10px 12px;
      background: rgba(0, 0, 0, 0.35);
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 3px;
      color: #fff;
      font-size: 13px;
    }

    #skin-market .sm-status {
      min-height: 18px;
      margin-bottom: 14px;
      color: #8f9688;
      font-size: 12px;
      letter-spacing: 1px;
    }

    #skin-market .sm-status.sm-error {
      color: #ff8a8a;
    }

    #skin-market .sm-content {
      display: grid;
      grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
      gap: 18px;
    }

    #skin-market .sm-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-content: start;
    }

    #skin-market .sm-card {
      padding: 14px;
      border-radius: 5px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(255, 255, 255, 0.03);
      cursor: pointer;
    }

    #skin-market .sm-card:hover,
    #skin-market .sm-card.sm-selected {
      border-color: rgba(216, 209, 154, 0.65);
      background: rgba(216, 209, 154, 0.08);
    }

    #skin-market .sm-card-title {
      font-size: 15px;
      font-weight: bold;
      line-height: 1.3;
      margin-bottom: 6px;
    }

    #skin-market .sm-chip-row {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      margin-bottom: 8px;
    }

    #skin-market .sm-chip {
      display: inline-flex;
      align-items: center;
      padding: 3px 7px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.08);
      color: #d9dbd7;
      font-size: 10px;
      letter-spacing: 0.8px;
      text-transform: uppercase;
    }

    #skin-market .sm-meta,
    #skin-market .sm-detail-list {
      display: grid;
      gap: 6px;
    }

    #skin-market .sm-meta-row,
    #skin-market .sm-detail-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      color: #c3c6c0;
      font-size: 12px;
    }

    #skin-market .sm-muted {
      color: #767d71;
    }

    #skin-market .sm-price {
      color: #66d17a;
      font-weight: bold;
      font-size: 18px;
      margin-top: 4px;
    }

    #skin-market .sm-detail {
      min-height: 320px;
      padding: 18px;
      border-radius: 5px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(0, 0, 0, 0.22);
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    #skin-market .sm-preview {
      min-height: 180px;
      border-radius: 5px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: radial-gradient(circle at top, rgba(216, 209, 154, 0.1), transparent 45%), rgba(0, 0, 0, 0.18);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    #skin-market .sm-check {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #c3c6c0;
      font-size: 12px;
      letter-spacing: 0.8px;
      text-transform: uppercase;
    }

    #skin-market .sm-empty {
      padding: 32px 18px;
      text-align: center;
      color: #8b9185;
      border: 1px dashed rgba(255, 255, 255, 0.12);
      border-radius: 5px;
      background: rgba(255, 255, 255, 0.02);
      letter-spacing: 1px;
      font-size: 12px;
    }

    #skin-market .sm-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      margin-top: 18px;
      color: #8f9688;
      font-size: 12px;
    }

    #skin-market .sm-buy-btn {
      display: block;
      width: 100%;
      margin-top: 16px;
      padding: 12px;
      background: linear-gradient(135deg, #4a7c3f, #2d5a1e);
      border: 1px solid rgba(120, 200, 100, 0.4);
      border-radius: 4px;
      color: #fff;
      font-family: 'Courier New', monospace;
      font-size: 16px;
      font-weight: bold;
      letter-spacing: 2px;
      cursor: pointer;
      transition: all 0.2s;
    }
    #skin-market .sm-buy-btn:hover {
      background: linear-gradient(135deg, #5a9c4f, #3d7a2e);
      border-color: rgba(120, 200, 100, 0.7);
    }

    #skin-market .sm-cancel-btn {
      display: block;
      width: 100%;
      margin-top: 10px;
      padding: 10px;
      background: rgba(132, 56, 56, 0.18);
      border: 1px solid rgba(220, 120, 120, 0.45);
      border-radius: 4px;
      color: #f4cdcd;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      cursor: pointer;
      transition: all 0.15s;
    }
    #skin-market .sm-cancel-btn:hover:not(:disabled) {
      background: rgba(176, 76, 76, 0.32);
      border-color: rgba(240, 140, 140, 0.7);
      color: #fff;
    }
    #skin-market .sm-cancel-btn:disabled { opacity: 0.5; cursor: default; }

    #skin-market .sm-lock-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 12px;
      padding: 8px 10px;
      background: rgba(216, 209, 154, 0.08);
      border: 1px solid rgba(216, 209, 154, 0.32);
      border-radius: 4px;
      color: #d8d19a;
      font-size: 11px;
      letter-spacing: 0.6px;
    }
    #skin-market .sm-lock-banner.sm-lock-active {
      background: rgba(176, 96, 60, 0.12);
      border-color: rgba(220, 140, 96, 0.45);
      color: #f0c79c;
    }
    #skin-market .sm-lock-icon { font-size: 14px; }

    #skin-market .sm-confirm-overlay {
      position: fixed;
      inset: 0;
      z-index: 525;
      display: none;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.72);
    }
    #skin-market .sm-confirm-overlay.sm-confirm-visible { display: flex; }
    #skin-market .sm-confirm-modal {
      width: min(440px, calc(100vw - 32px));
      padding: 22px;
      background: rgba(20, 24, 20, 0.98);
      border: 1px solid rgba(216, 209, 154, 0.32);
      border-radius: 6px;
      color: #fff;
      font-family: 'Courier New', monospace;
      box-shadow: 0 18px 42px rgba(0, 0, 0, 0.55);
    }
    #skin-market .sm-confirm-title {
      margin: 0 0 12px;
      color: #d8d19a;
      font-size: 16px;
      letter-spacing: 2px;
      text-transform: uppercase;
    }
    #skin-market .sm-confirm-summary {
      display: grid;
      gap: 6px;
      margin-bottom: 14px;
      font-size: 12px;
      color: #c3c6c0;
    }
    #skin-market .sm-confirm-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
    }
    #skin-market .sm-confirm-row.sm-total {
      margin-top: 4px;
      padding-top: 6px;
      border-top: 1px dashed rgba(255, 255, 255, 0.18);
      color: #fff;
      font-weight: bold;
    }
    #skin-market .sm-confirm-warning {
      margin: 8px 0 14px;
      padding: 8px 10px;
      background: rgba(176, 96, 60, 0.12);
      border-left: 3px solid rgba(220, 140, 96, 0.65);
      color: #f0c79c;
      font-size: 11px;
      line-height: 1.5;
    }
    #skin-market .sm-confirm-error {
      margin: 0 0 12px;
      padding: 8px 10px;
      background: rgba(180, 60, 60, 0.18);
      border-left: 3px solid rgba(240, 120, 120, 0.7);
      color: #ffd1d1;
      font-size: 11px;
    }
    #skin-market .sm-confirm-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
    }
    #skin-market .sm-confirm-btn {
      padding: 10px 16px;
      border-radius: 3px;
      border: 1px solid rgba(255, 255, 255, 0.16);
      background: rgba(255, 255, 255, 0.06);
      color: #d2d2cb;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      letter-spacing: 1.2px;
      text-transform: uppercase;
      cursor: pointer;
    }
    #skin-market .sm-confirm-btn.sm-confirm-primary {
      background: linear-gradient(135deg, #4a7c3f, #2d5a1e);
      border-color: rgba(120, 200, 100, 0.45);
      color: #fff;
    }
    #skin-market .sm-confirm-btn:disabled { opacity: 0.5; cursor: default; }

    #skin-market .sm-toast {
      position: fixed;
      bottom: 32px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 540;
      padding: 12px 18px;
      border-radius: 4px;
      background: rgba(40, 70, 36, 0.94);
      border: 1px solid rgba(140, 200, 120, 0.45);
      color: #e8f6df;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      letter-spacing: 0.8px;
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s;
    }
    #skin-market .sm-toast.sm-toast-visible { opacity: 1; }
    #skin-market .sm-toast.sm-toast-error {
      background: rgba(110, 36, 36, 0.95);
      border-color: rgba(240, 130, 130, 0.55);
      color: #ffe2e2;
    }

    #skin-market .sm-float-bar {
      margin-top: 6px;
      padding: 8px 10px;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.32);
      border: 1px solid rgba(255, 255, 255, 0.08);
    }
    #skin-market .sm-float-bar-track {
      position: relative;
      height: 12px;
      border-radius: 6px;
      background: linear-gradient(to right,
        #3aa55d 0%, #3aa55d 7%,
        #7bc35a 7%, #7bc35a 15%,
        #d8c861 15%, #d8c861 38%,
        #d68d3f 38%, #d68d3f 45%,
        #c5443d 45%, #c5443d 100%);
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.45);
    }
    #skin-market .sm-float-needle {
      position: absolute;
      top: -3px;
      width: 3px;
      height: 18px;
      background: #fff;
      border-radius: 2px;
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.85);
      transform: translateX(-50%);
    }
    #skin-market .sm-float-bar-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 6px;
      color: #8b9185;
      font-size: 9px;
      letter-spacing: 1px;
    }
    #skin-market .sm-float-bar-value {
      margin-top: 6px;
      color: #d8d19a;
      font-size: 11px;
      letter-spacing: 0.6px;
    }

    #skin-market .sm-stickers {
      display: grid;
      gap: 6px;
      margin-top: 4px;
    }
    #skin-market .sm-sticker-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      padding: 6px 8px;
      border-radius: 3px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.07);
      font-size: 11px;
    }
    #skin-market .sm-sticker-name {
      color: #e8eae3;
      font-weight: 600;
      letter-spacing: 0.4px;
    }
    #skin-market .sm-sticker-meta {
      color: #8b9185;
      font-size: 10px;
      letter-spacing: 0.5px;
    }
    #skin-market .sm-sticker-finish {
      display: inline-block;
      margin-left: 6px;
      padding: 1px 5px;
      border-radius: 999px;
      font-size: 9px;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }
    #skin-market .sm-sticker-finish.holo { background: rgba(216, 100, 220, 0.18); color: #f0c9f6; }
    #skin-market .sm-sticker-finish.foil { background: rgba(160, 200, 220, 0.18); color: #d8edf6; }
    #skin-market .sm-sticker-finish.glitter { background: rgba(255, 225, 110, 0.18); color: #f7e495; }

    #skin-market .sm-insights {
      margin-top: 12px;
      padding: 10px 12px;
      border-radius: 5px;
      border: 1px solid rgba(216, 209, 154, 0.22);
      background: rgba(216, 209, 154, 0.05);
    }
    #skin-market .sm-insights-title {
      margin: 0 0 8px;
      color: #d8d19a;
      font-size: 11px;
      letter-spacing: 1.4px;
      text-transform: uppercase;
    }
    #skin-market .sm-insights-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px 12px;
      font-size: 12px;
      color: #c3c6c0;
    }
    #skin-market .sm-insights-row {
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }
    #skin-market .sm-spark {
      margin-top: 10px;
      display: flex;
      align-items: flex-end;
      gap: 3px;
      height: 36px;
    }
    #skin-market .sm-spark-bar {
      flex: 1;
      min-width: 4px;
      background: linear-gradient(to top, rgba(216, 209, 154, 0.85), rgba(216, 209, 154, 0.35));
      border-radius: 2px 2px 0 0;
    }
    #skin-market .sm-spark-empty {
      color: #767d71;
      font-size: 11px;
      letter-spacing: 0.6px;
    }

    @media (max-width: 900px) {
      #skin-market .sm-content {
        grid-template-columns: 1fr;
      }

      #skin-market .sm-panel {
        width: calc(100vw - 24px);
        margin: 12px auto;
        padding: 16px;
      }

      #skin-market .sm-header {
        flex-direction: column;
      }

      #skin-market .sm-actions {
        justify-content: flex-start;
      }
    }
  `;
  document.head.appendChild(style);
}

function rarityColor(rarity: SkinRarity): string {
  return `#${RARITY_COLORS[rarity].toString(16).padStart(6, '0')}`;
}

function formatPrice(price: number): string {
  return `$${price.toLocaleString()}`;
}

function formatDate(timestamp: number): string {
  try {
    return new Date(timestamp).toLocaleDateString();
  } catch {
    return 'Unknown';
  }
}

function clearElement(el: HTMLElement): void {
  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }
}

function appendTextElement<K extends keyof HTMLElementTagNameMap>(
  parent: HTMLElement,
  tag: K,
  className: string,
  text: string,
  color?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  el.className = className;
  el.textContent = text;
  if (color) el.style.color = color;
  parent.appendChild(el);
  return el;
}

function appendChipRow(parent: HTMLElement, values: string[]): void {
  const row = document.createElement('div');
  row.className = 'sm-chip-row';
  for (const value of values) {
    appendTextElement(row, 'span', 'sm-chip', value);
  }
  parent.appendChild(row);
}

function appendLabeledRow(parent: HTMLElement, className: string, label: string, value: string): void {
  const row = document.createElement('div');
  row.className = className;
  appendTextElement(row, 'span', 'sm-muted', label);
  appendTextElement(row, 'span', '', value);
  parent.appendChild(row);
}

function appendEmptyState(parent: HTMLElement, message: string): void {
  const empty = document.createElement('div');
  empty.className = 'sm-empty';
  empty.textContent = message;
  parent.appendChild(empty);
}

function buildSearchUrl(baseUrl: string, filters: MarketSearchFilters): string {
  const url = new URL('/api/market/search', `${baseUrl}/`);
  const entries = Object.entries(filters) as Array<[keyof MarketSearchFilters, MarketSearchFilters[keyof MarketSearchFilters]]>;
  for (const [key, value] of entries) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(String(key), String(value));
  }
  return url.toString();
}

function normalizeQuery(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function hashSeed(seed: number): number {
  let value = seed | 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return Math.abs(value);
}

function seededNumber(seed: number, min: number, max: number): number {
  if (max <= min) return min;
  const normalized = (hashSeed(seed) % 10_000) / 9_999;
  return min + (max - min) * normalized;
}

function seededInteger(seed: number, min: number, max: number): number {
  return Math.round(seededNumber(seed, min, max));
}

function buildFallbackListing(skinId: number): MarketListing {
  const skin = weaponSkinRegistry.getSkin(skinId);
  if (!skin) {
    throw new Error(`Unknown skin ${skinId}`);
  }

  const weaponId = skin.weaponIds[0] ?? 'unknown';
  const [minWear, maxWear] = skin.wearRange;
  const wearFloat = Number(seededNumber(skin.id * 11, minWear, maxWear).toFixed(3));
  const isStatTrak = skin.statTrakEligible && hashSeed(skin.id * 17) % 3 === 0;
  const rarityBase: Record<SkinRarity, number> = {
    consumer: 180,
    industrial: 320,
    'mil-spec': 750,
    restricted: 1600,
    classified: 4200,
    covert: 9800,
    contraband: 22000,
    special: 18000,
  };
  const price = Math.round(
    rarityBase[skin.rarity] +
    seededNumber(skin.id * 19, 50, 420) +
    (1 - wearFloat) * 900 +
    (isStatTrak ? 1400 : 0),
  );
  const sellerName = FALLBACK_SELLERS[hashSeed(skin.id * 23) % FALLBACK_SELLERS.length] ?? 'Frontline Finds';
  const listedAt = Date.UTC(2026, 2, 1) + seededInteger(skin.id * 29, 0, 21 * 24 * 60 * 60 * 1000);

  return {
    listingId: `showcase-${skin.id}`,
    sellerAccountId: `showcase-seller-${hashSeed(skin.id * 31) % FALLBACK_SELLERS.length}`,
    sellerName,
    instanceId: `showcase-instance-${skin.id}`,
    skinId: skin.id,
    skinName: skin.name,
    weaponId,
    wearFloat,
    patternSeed: skin.pattern ? seededInteger(skin.id * 37, skin.pattern.seedRange[0], skin.pattern.seedRange[1]) : 0,
    isStatTrak,
    statTrakKills: isStatTrak ? seededInteger(skin.id * 41, 12, 420) : -1,
    nameTag: hashSeed(skin.id * 43) % 5 === 0 ? `${skin.collection} Pick` : '',
    rarity: skin.rarity,
    price,
    listedAt,
    status: 'active',
    stickers: buildListingStickers(skin.id),
  };
}

function createFallbackResponse(filters: MarketSearchFilters): MarketSearchResponse {
  const skinNameQuery = normalizeQuery(filters.skinName);
  const weaponQuery = normalizeQuery(filters.weaponId);
  const limit = Math.min(DEFAULT_LIMIT, Math.max(1, filters.limit ?? DEFAULT_LIMIT));
  const offset = Math.max(0, filters.offset ?? 0);

  const listings = weaponSkinRegistry.getAll()
    .map((skin) => buildFallbackListing(skin.id))
    .filter((listing) => {
      if (skinNameQuery && !listing.skinName.toLowerCase().includes(skinNameQuery)) return false;
      if (weaponQuery && !listing.weaponId.toLowerCase().includes(weaponQuery)) return false;
      if (filters.rarity && listing.rarity !== filters.rarity) return false;
      if (filters.wearLevel && wearLevelFromFloat(listing.wearFloat) !== filters.wearLevel) return false;
      if (filters.minWear !== undefined && listing.wearFloat < filters.minWear) return false;
      if (filters.maxWear !== undefined && listing.wearFloat > filters.maxWear) return false;
      if (filters.minPrice !== undefined && listing.price < filters.minPrice) return false;
      if (filters.maxPrice !== undefined && listing.price > filters.maxPrice) return false;
      if (filters.statTrakOnly && !listing.isStatTrak) return false;
      return true;
    });

  const sorted = listings.sort((a, b) => {
    switch (filters.sortBy ?? DEFAULT_SORT) {
      case 'listed-asc': return a.listedAt - b.listedAt;
      case 'price-asc': return a.price - b.price;
      case 'price-desc': return b.price - a.price;
      case 'wear-asc': return a.wearFloat - b.wearFloat;
      case 'wear-desc': return b.wearFloat - a.wearFloat;
      case 'listed-desc':
      default:
        return b.listedAt - a.listedAt;
    }
  });

  return {
    listings: sorted.slice(offset, offset + limit),
    totalCount: sorted.length,
    offset,
    limit,
  };
}

interface SkinMarketUIOptions {
  previewRenderer?: SkinPreviewRenderer;
  skinLoader?: WeaponSkinLoader;
  /**
   * BUG-W-17: provide the local player's current balance so the confirm
   * modal can show "Your balance: $X" and disable the Confirm button when
   * the user can't afford the listing. Falls back to
   * `(window as any).getPlayerMoney?.()` when omitted.
   */
  balanceProvider?: () => number;
}

/**
 * Marketplace UI controller.
 * Manages the browse-first skin marketplace interface.
 */
export class SkinMarketUI {
  private readonly apiBaseUrl: string;
  private previewRenderer?: SkinPreviewRenderer;
  private readonly skinLoader?: WeaponSkinLoader;
  private readonly balanceProvider?: () => number;
  private container: HTMLElement | null = null;
  private gridEl: HTMLElement | null = null;
  private detailEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private summaryEl: HTMLElement | null = null;
  private searchInput: HTMLInputElement | null = null;
  private weaponInput: HTMLInputElement | null = null;
  private raritySelect: HTMLSelectElement | null = null;
  private sortSelect: HTMLSelectElement | null = null;
  private pagePrevBtn: HTMLButtonElement | null = null;
  private pageNextBtn: HTMLButtonElement | null = null;
  private sellWeaponSelect: HTMLSelectElement | null = null;
  private sellSkinSelect: HTMLSelectElement | null = null;
  private sellPriceInput: HTMLInputElement | null = null;
  private sellNameTagInput: HTMLInputElement | null = null;
  private sellStatTrakInput: HTMLInputElement | null = null;
  private wearLevelSelect: HTMLSelectElement | null = null;
  private filterToggleBtn: HTMLButtonElement | null = null;
  private filterBackdropEl: HTMLElement | null = null;
  private confirmOverlay: HTMLElement | null = null;
  private confirmModal: HTMLElement | null = null;
  private toastEl: HTMLElement | null = null;
  private toastTimer: number | null = null;
  private listings: MarketListing[] = [];
  private selectedListingId: string | null = null;
  private filters: MarketSearchFilters = {
    sortBy: DEFAULT_SORT,
    offset: 0,
    limit: DEFAULT_LIMIT,
  };
  private visible = false;
  private filterDrawerOpen = false;
  private loading = false;
  private totalCount = 0;
  private errorMessage = '';
  private sourceMessage = '';
  private requestToken = 0;
  private previewRequestToken = 0;
  private onBack: (() => void) | null = null;
  private remoteUnavailable = false;
  private ownAccountId: string | null = null;
  private confirmingListing: MarketListing | null = null;
  private confirmInFlight = false;
  private confirmError = '';
  private abortController: AbortController = new AbortController();
  /**
   * UI-C1: render-scope AbortController for per-render listeners on listing
   * cards, buy/cancel buttons, and the confirm modal. Recycled at the top of
   * render() so the controller's abort-callback chain doesn't pin DOM nodes
   * from previous renders.
   */
  private renderController: AbortController = new AbortController();

  constructor(apiBaseUrl: string, options: SkinMarketUIOptions = {}) {
    this.apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    this.previewRenderer = options.previewRenderer;
    this.skinLoader = options.skinLoader;
    this.balanceProvider = options.balanceProvider;
  }

  /**
   * BUG-W-17: resolve the local player's purchase-currency balance.
   * Priority: explicit `balanceProvider` option > `window.getPlayerMoney()`
   * shim > `null` (caller refused to surface money, treat as unknown).
   */
  private getPlayerBalance(): number | null {
    try {
      if (this.balanceProvider) {
        const value = this.balanceProvider();
        return Number.isFinite(value) ? value : null;
      }
      const shim = (globalThis as { getPlayerMoney?: () => number }).getPlayerMoney;
      if (typeof shim === 'function') {
        const value = shim();
        return Number.isFinite(value) ? value : null;
      }
    } catch {
      return null;
    }
    return null;
  }

  /**
   * Inject the preview renderer after construction. Allows the caller to
   * defer allocating the WebGL renderer until the market is actually opened.
   * Re-assignment replaces any prior renderer.
   */
  setPreviewRenderer(renderer: SkinPreviewRenderer): void {
    this.previewRenderer = renderer;
  }

  get isVisible(): boolean { return this.visible; }

  /**
   * Test-accessible accessor for the AbortSignal that gates every static
   * addEventListener registration on this panel. Used by tests to verify
   * that dispose() tears down all DOM listeners (UI-C1).
   */
  getListenerSignal(): AbortSignal {
    return this.abortController.signal;
  }

  /**
   * Test-accessible accessor for the render-scope AbortSignal recycled at the
   * top of render(). Used by tests to verify per-render listener cleanup.
   */
  getRenderListenerSignal(): AbortSignal {
    return this.renderController.signal;
  }

  init(parentElement: HTMLElement): void {
    injectStyles();
    // Re-init: abort prior listeners before swapping in fresh controllers so
    // closures from the old DOM tree don't leak.
    this.abortController.abort();
    this.renderController.abort();
    this.abortController = new AbortController();
    this.renderController = new AbortController();
    this.container = document.createElement('div');
    this.container.id = 'skin-market';
    if (isMobile()) this.container.classList.add('sm-mobile-market');
    this.container.innerHTML = `
      <div class="sm-panel">
        <div class="sm-header">
          <div>
            <h2 class="sm-title">Marketplace</h2>
            <div class="sm-subtitle">
              Browse community skin listings. This runtime pass stays browse-first and degrades cleanly when the market API is unavailable.
            </div>
          </div>
          <div class="sm-actions">
            <button class="sm-btn sm-filter-toggle" data-action="filters">Filter</button>
            <button class="sm-btn sm-btn-primary" data-action="refresh">Refresh</button>
            <button class="sm-btn" data-action="back">Back</button>
          </div>
        </div>

        <div class="sm-filter-backdrop" data-role="filter-backdrop"></div>
        <div class="sm-filters">
          <label class="sm-field">
            <span class="sm-label">Search</span>
            <input class="sm-input" data-filter="skinName" type="text" placeholder="Skin name" />
          </label>
          <label class="sm-field">
            <span class="sm-label">Weapon</span>
            <input class="sm-input" data-filter="weaponId" type="text" placeholder="ak47, awp, deagle..." />
          </label>
          <label class="sm-field">
            <span class="sm-label">Rarity</span>
            <select class="sm-select" data-filter="rarity">
              <option value="">All rarities</option>
              <option value="consumer">Consumer</option>
              <option value="industrial">Industrial</option>
              <option value="mil-spec">Mil-Spec</option>
              <option value="restricted">Restricted</option>
              <option value="classified">Classified</option>
              <option value="covert">Covert</option>
              <option value="contraband">Contraband</option>
              <option value="special">Special</option>
            </select>
          </label>
          <label class="sm-field">
            <span class="sm-label">Wear</span>
            <select class="sm-select" data-filter="wearLevel">
              <option value="">Any wear</option>
              <option value="factory-new">Factory New</option>
              <option value="minimal-wear">Minimal Wear</option>
              <option value="field-tested">Field-Tested</option>
              <option value="well-worn">Well-Worn</option>
              <option value="battle-scarred">Battle-Scarred</option>
            </select>
          </label>
          <label class="sm-field">
            <span class="sm-label">Sort</span>
            <select class="sm-select" data-filter="sortBy">
              <option value="listed-desc">Newest</option>
              <option value="listed-asc">Oldest</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="wear-asc">Wear: Low to High</option>
              <option value="wear-desc">Wear: High to Low</option>
            </select>
          </label>
        </div>

        <div class="sm-sell">
          <div class="sm-subtitle">List one of your skins for sale in the local runtime market.</div>
          <div class="sm-sell-grid">
            <label class="sm-field">
              <span class="sm-label">Sell Weapon</span>
              <select class="sm-select" data-sell="weapon"></select>
            </label>
            <label class="sm-field">
              <span class="sm-label">Sell Skin</span>
              <select class="sm-select" data-sell="skin"></select>
            </label>
            <label class="sm-field">
              <span class="sm-label">Price</span>
              <input class="sm-input" data-sell="price" type="number" min="1" max="999999" value="1200" />
            </label>
            <label class="sm-field">
              <span class="sm-label">Name Tag</span>
              <input class="sm-input" data-sell="nameTag" type="text" maxlength="20" placeholder="Optional custom tag" />
            </label>
            <label class="sm-check">
              <input data-sell="statTrak" type="checkbox" />
              <span>StatTrak</span>
            </label>
            <button class="sm-btn sm-btn-primary" data-action="list">List For Sale</button>
          </div>
        </div>

        <div class="sm-status" data-role="status"></div>

        <div class="sm-content">
          <div class="sm-grid" data-role="grid"></div>
          <div class="sm-detail" data-role="detail"></div>
        </div>

        <div class="sm-footer">
          <div data-role="summary">0 listings</div>
          <div class="sm-actions">
            <button class="sm-btn" data-action="prev">Previous</button>
            <button class="sm-btn" data-action="next">Next</button>
          </div>
        </div>

        <div class="sm-confirm-overlay" data-role="confirm-overlay" role="dialog" aria-modal="true" aria-hidden="true">
          <div class="sm-confirm-modal" data-role="confirm-modal"></div>
        </div>

        <div class="sm-toast" data-role="toast" role="status" aria-live="polite"></div>
      </div>
    `;
    parentElement.appendChild(this.container);

    this.gridEl = this.container.querySelector('[data-role="grid"]');
    this.detailEl = this.container.querySelector('[data-role="detail"]');
    this.statusEl = this.container.querySelector('[data-role="status"]');
    this.summaryEl = this.container.querySelector('[data-role="summary"]');
    this.searchInput = this.container.querySelector('input[data-filter="skinName"]');
    this.weaponInput = this.container.querySelector('input[data-filter="weaponId"]');
    this.raritySelect = this.container.querySelector('select[data-filter="rarity"]');
    this.wearLevelSelect = this.container.querySelector('select[data-filter="wearLevel"]');
    this.sortSelect = this.container.querySelector('select[data-filter="sortBy"]');
    this.filterToggleBtn = this.container.querySelector('button[data-action="filters"]');
    this.filterBackdropEl = this.container.querySelector('[data-role="filter-backdrop"]');
    this.confirmOverlay = this.container.querySelector('[data-role="confirm-overlay"]');
    this.confirmModal = this.container.querySelector('[data-role="confirm-modal"]');
    this.toastEl = this.container.querySelector('[data-role="toast"]');
    this.pagePrevBtn = this.container.querySelector('button[data-action="prev"]');
    this.pageNextBtn = this.container.querySelector('button[data-action="next"]');
    this.sellWeaponSelect = this.container.querySelector('select[data-sell="weapon"]');
    this.sellSkinSelect = this.container.querySelector('select[data-sell="skin"]');
    this.sellPriceInput = this.container.querySelector('input[data-sell="price"]');
    this.sellNameTagInput = this.container.querySelector('input[data-sell="nameTag"]');
    this.sellStatTrakInput = this.container.querySelector('input[data-sell="statTrak"]');

    const signal = this.abortController.signal;
    this.container.querySelector('button[data-action="refresh"]')?.addEventListener('click', () => {
      void this.loadListings(true);
    }, { signal });
    this.container.querySelector('button[data-action="back"]')?.addEventListener('click', () => {
      this.hide();
      this.onBack?.();
    }, { signal });
    this.filterToggleBtn?.addEventListener('click', () => {
      this.setFilterDrawerOpen(!this.filterDrawerOpen);
    }, { signal });
    this.filterBackdropEl?.addEventListener('click', () => {
      this.setFilterDrawerOpen(false);
    }, { signal });
    this.container.querySelector('button[data-action="list"]')?.addEventListener('click', () => {
      void this.handleCreateListing();
    }, { signal });

    this.pagePrevBtn?.addEventListener('click', () => {
      const currentOffset = this.filters.offset ?? 0;
      if (currentOffset <= 0) return;
      this.filters.offset = Math.max(0, currentOffset - (this.filters.limit ?? DEFAULT_PAGE_SIZE));
      void this.loadListings();
    }, { signal });
    this.pageNextBtn?.addEventListener('click', () => {
      const nextOffset = (this.filters.offset ?? 0) + (this.filters.limit ?? DEFAULT_PAGE_SIZE);
      if (nextOffset >= this.totalCount) return;
      this.filters.offset = nextOffset;
      void this.loadListings();
    }, { signal });

    this.searchInput?.addEventListener('input', () => {
      this.updateFilter('skinName', this.searchInput?.value.trim() ?? '');
    }, { signal });
    this.weaponInput?.addEventListener('input', () => {
      this.updateFilter('weaponId', this.weaponInput?.value.trim() ?? '');
    }, { signal });
    this.raritySelect?.addEventListener('change', () => {
      this.updateFilter('rarity', (this.raritySelect?.value ?? '') as MarketSearchFilters['rarity']);
    }, { signal });
    this.wearLevelSelect?.addEventListener('change', () => {
      this.updateFilter('wearLevel', (this.wearLevelSelect?.value ?? '') as WearLevel | '');
    }, { signal });
    this.sortSelect?.addEventListener('change', () => {
      this.updateFilter('sortBy', (this.sortSelect?.value ?? DEFAULT_SORT) as MarketSearchFilters['sortBy']);
    }, { signal });

    this.confirmOverlay?.addEventListener('click', (event) => {
      if ((event as MouseEvent).target === this.confirmOverlay) {
        this.closeConfirmModal();
      }
    }, { signal });
    this.sellWeaponSelect?.addEventListener('change', () => {
      this.populateSellSkinOptions();
    }, { signal });
    this.sellSkinSelect?.addEventListener('change', () => {
      this.seedSellPrice();
    }, { signal });

    this.populateSellWeaponOptions();
    this.populateSellSkinOptions();
    this.render();
  }

  show(): void {
    if (!this.container) return;
    this.visible = true;
    this.container.style.display = 'flex';
    void this.loadListings();
  }

  hide(): void {
    if (!this.container) return;
    this.visible = false;
    this.setFilterDrawerOpen(false);
    this.container.style.display = 'none';
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  setFilters(filters: MarketSearchFilters): void {
    this.filters = { ...this.filters, ...filters };
    this.syncFilterControls();
    if (this.visible) {
      void this.loadListings();
    }
  }

  getFilters(): MarketSearchFilters {
    return { ...this.filters };
  }

  updateListings(response: MarketSearchResponse): void {
    this.listings = response.listings;
    this.totalCount = response.totalCount;
    this.filters.offset = response.offset ?? 0;
    this.filters.limit = response.limit ?? this.filters.limit ?? DEFAULT_PAGE_SIZE;
    this.selectedListingId = this.listings[0]?.listingId ?? null;
    this.loading = false;
    this.errorMessage = '';
    this.render();
  }

  getListings(): readonly MarketListing[] {
    return this.listings;
  }

  setOnBack(callback: (() => void) | null): void {
    this.onBack = callback;
  }

  dispose(): void {
    this.abortController.abort();
    this.renderController.abort();
    if (this.toastTimer !== null) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
    this.gridEl = null;
    this.detailEl = null;
    this.statusEl = null;
    this.summaryEl = null;
    this.searchInput = null;
    this.weaponInput = null;
    this.raritySelect = null;
    this.wearLevelSelect = null;
    this.sortSelect = null;
    this.filterToggleBtn = null;
    this.filterBackdropEl = null;
    this.pagePrevBtn = null;
    this.pageNextBtn = null;
    this.sellWeaponSelect = null;
    this.sellSkinSelect = null;
    this.sellPriceInput = null;
    this.sellNameTagInput = null;
    this.sellStatTrakInput = null;
    this.confirmOverlay = null;
    this.confirmModal = null;
    this.toastEl = null;
    this.confirmingListing = null;
    this.listings = [];
    this.onBack = null;
  }

  /**
   * Tell the marketplace which account is the local user. Listings whose
   * sellerAccountId matches will surface a Cancel button instead of Buy.
   * Pass null to clear (e.g. on logout).
   */
  setOwnAccount(accountId: string | null): void {
    this.ownAccountId = accountId;
    if (this.visible) this.render();
  }

  private populateSellWeaponOptions(): void {
    if (!this.sellWeaponSelect) return;
    clearElement(this.sellWeaponSelect);

    const weapons = Array.from(new Set(
      weaponSkinRegistry.getAll().map((skin) => skin.weaponIds[0]).filter(Boolean),
    )).sort();

    for (const weaponId of weapons) {
      const option = document.createElement('option');
      option.value = weaponId;
      option.textContent = weaponId;
      this.sellWeaponSelect.appendChild(option);
    }
    if (!this.sellWeaponSelect.value && weapons[0]) {
      this.sellWeaponSelect.value = weapons[0];
    }
  }

  private populateSellSkinOptions(): void {
    if (!this.sellSkinSelect) return;
    clearElement(this.sellSkinSelect);

    const weaponId = this.sellWeaponSelect?.value || weaponSkinRegistry.getAll()[0]?.weaponIds[0] || 'ak47';
    const skins = weaponSkinRegistry.getSkinsForWeapon(weaponId);
    for (const skin of skins) {
      const option = document.createElement('option');
      option.value = String(skin.id);
      option.textContent = skin.name;
      this.sellSkinSelect.appendChild(option);
    }
    if (!this.sellSkinSelect.value && skins[0]) {
      this.sellSkinSelect.value = String(skins[0].id);
    }
    this.seedSellPrice();
  }

  private seedSellPrice(): void {
    if (!this.sellPriceInput || !this.sellSkinSelect) return;
    const skinId = Number(this.sellSkinSelect.value);
    if (!skinId) return;
    const listing = buildFallbackListing(skinId);
    this.sellPriceInput.value = String(listing.price);
  }

  private updateFilter<K extends keyof MarketSearchFilters>(key: K, value: MarketSearchFilters[K] | ''): void {
    if (value === '' || value === undefined || value === null) {
      delete this.filters[key];
    } else {
      this.filters[key] = value;
    }
    this.filters.offset = 0;
    void this.loadListings();
  }

  private setFilterDrawerOpen(open: boolean): void {
    this.filterDrawerOpen = open;
    if (!this.container) return;
    if (open) {
      this.container.classList.add('sm-filter-open');
    } else {
      this.container.classList.remove('sm-filter-open');
    }
  }

  private syncFilterControls(): void {
    if (this.searchInput) this.searchInput.value = this.filters.skinName ?? '';
    if (this.weaponInput) this.weaponInput.value = this.filters.weaponId ?? '';
    if (this.raritySelect) this.raritySelect.value = this.filters.rarity ?? '';
    if (this.wearLevelSelect) this.wearLevelSelect.value = this.filters.wearLevel ?? '';
    if (this.sortSelect) this.sortSelect.value = this.filters.sortBy ?? DEFAULT_SORT;
  }

  private async loadListings(forceRemote = false): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    this.render();

    const requestToken = ++this.requestToken;
    if (this.remoteUnavailable && !forceRemote) {
      this.applyFallbackListings(requestToken);
      return;
    }

    try {
      const url = buildSearchUrl(this.apiBaseUrl, {
        ...this.filters,
        sortBy: this.filters.sortBy ?? DEFAULT_SORT,
        offset: this.filters.offset ?? 0,
        limit: this.filters.limit ?? DEFAULT_LIMIT,
      });
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Marketplace unavailable (${response.status})`);
      }

      const payload = await response.json() as Partial<MarketSearchResponse>;
      if (requestToken !== this.requestToken) return;
      this.remoteUnavailable = false;
      this.sourceMessage = '';

      this.updateListings({
        listings: Array.isArray(payload.listings) ? payload.listings : [],
        totalCount: typeof payload.totalCount === 'number' ? payload.totalCount : 0,
        offset: typeof payload.offset === 'number' ? payload.offset : (this.filters.offset ?? 0),
        limit: typeof payload.limit === 'number' ? payload.limit : (this.filters.limit ?? DEFAULT_LIMIT),
      });
    } catch (error) {
      if (requestToken !== this.requestToken) return;
      this.remoteUnavailable = true;
      this.sourceMessage = error instanceof Error
        ? `${error.message}. ${FALLBACK_SOURCE_MESSAGE}`
        : `Marketplace unavailable. ${FALLBACK_SOURCE_MESSAGE}`;
      this.applyFallbackListings(requestToken);
    }
  }

  private applyFallbackListings(requestToken: number): void {
    const response = createFallbackResponse({
      ...this.filters,
      sortBy: this.filters.sortBy ?? DEFAULT_SORT,
      offset: this.filters.offset ?? 0,
      limit: this.filters.limit ?? DEFAULT_LIMIT,
    });
    if (requestToken !== this.requestToken) return;
    this.updateListings(response);
  }

  private render(): void {
    // Recycle the render-scope controller so listeners attached during the
    // previous render() are released along with their DOM nodes (UI-C1).
    this.renderController.abort();
    this.renderController = new AbortController();
    this.syncFilterControls();
    this.renderStatus();
    this.renderGrid();
    this.renderDetail();
    this.renderFooter();
  }

  private renderStatus(): void {
    if (!this.statusEl) return;
    if (this.loading) {
      this.statusEl.className = 'sm-status';
      this.statusEl.textContent = 'Loading marketplace listings...';
      return;
    }
    if (this.errorMessage) {
      this.statusEl.className = 'sm-status sm-error';
      this.statusEl.textContent = this.errorMessage;
      return;
    }
    this.statusEl.className = 'sm-status';
    if (this.sourceMessage) {
      this.statusEl.textContent = this.totalCount > 0
        ? `${this.sourceMessage} Showing ${this.listings.length} of ${this.totalCount} listings.`
        : `${this.sourceMessage} No listings match the current filters.`;
      return;
    }
    this.statusEl.textContent = this.totalCount > 0
      ? `Showing ${this.listings.length} of ${this.totalCount} listings`
      : 'Browse the market by skin name, weapon, rarity, or sort order.';
  }

  private renderGrid(): void {
    if (!this.gridEl) return;
    clearElement(this.gridEl);

    if (this.loading) {
      appendEmptyState(this.gridEl, 'Loading listings…');
      return;
    }
    if (this.listings.length === 0) {
      appendEmptyState(this.gridEl, 'No listings match the current filters.');
      return;
    }

    for (const listing of this.listings) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = `sm-card${listing.listingId === this.selectedListingId ? ' sm-selected' : ''}`;
      card.dataset.listingId = listing.listingId;
      appendTextElement(card, 'div', 'sm-card-title', listing.skinName, rarityColor(listing.rarity));
      const chips = [
        listing.weaponId,
        wearLevelDisplayName(wearLevelFromFloat(listing.wearFloat)),
      ];
      if (listing.isStatTrak) chips.push('StatTrak');
      const collection = weaponSkinRegistry.getSkin(listing.skinId)?.collection;
      if (collection) chips.push(collection);
      appendChipRow(card, chips);

      const meta = document.createElement('div');
      meta.className = 'sm-meta';
      appendLabeledRow(meta, 'sm-meta-row', 'Seller', listing.sellerName);
      appendLabeledRow(meta, 'sm-meta-row', 'Listed', formatDate(listing.listedAt));
      appendLabeledRow(meta, 'sm-meta-row', 'Float', listing.wearFloat.toFixed(3));
      const stickerCount = listing.stickers?.filter(Boolean).length ?? 0;
      if (stickerCount > 0) {
        appendLabeledRow(meta, 'sm-meta-row', 'Stickers', String(stickerCount));
      }
      card.appendChild(meta);

      appendTextElement(card, 'div', 'sm-price', formatPrice(listing.price));
      card.addEventListener('click', () => {
        this.selectedListingId = listing.listingId;
        this.render();
      }, { signal: this.renderController.signal });
      this.gridEl.appendChild(card);
    }
  }

  private renderDetail(): void {
    if (!this.detailEl) return;
    const selected = this.listings.find((listing) => listing.listingId === this.selectedListingId) ?? this.listings[0];
    if (!selected) {
      clearElement(this.detailEl);
      appendEmptyState(this.detailEl, 'Select a listing to inspect its details.');
      return;
    }

    this.selectedListingId = selected.listingId;
    const isOwnListing = !!this.ownAccountId && selected.sellerAccountId === this.ownAccountId;
    const canPurchase = !this.sourceMessage && selected.status === 'active' && !isOwnListing;
    const canCancel = !this.sourceMessage && selected.status === 'active' && isOwnListing;
    const skinDef = weaponSkinRegistry.getSkin(selected.skinId);
    clearElement(this.detailEl);
    appendTextElement(this.detailEl, 'div', 'sm-card-title', selected.skinName, rarityColor(selected.rarity));
    const detailChips = [
      selected.weaponId,
      selected.rarity,
      wearLevelDisplayName(wearLevelFromFloat(selected.wearFloat)),
    ];
    if (selected.isStatTrak) detailChips.push('StatTrak');
    if (skinDef?.collection) detailChips.push(skinDef.collection);
    appendChipRow(this.detailEl, detailChips);
    appendTextElement(this.detailEl, 'div', 'sm-price', formatPrice(selected.price));

    if (this.previewRenderer && this.skinLoader) {
      const previewEl = document.createElement('div');
      previewEl.className = 'sm-preview';
      previewEl.textContent = 'Rendering preview…';
      this.detailEl.appendChild(previewEl);
      void this.renderListingPreview(selected, previewEl);
    }

    this.appendFloatBar(this.detailEl, selected, skinDef?.wearRange);

    const detailList = document.createElement('div');
    detailList.className = 'sm-detail-list';
    appendLabeledRow(detailList, 'sm-detail-row', 'Seller', selected.sellerName);
    appendLabeledRow(detailList, 'sm-detail-row', 'Wear Float', selected.wearFloat.toFixed(3));
    appendLabeledRow(detailList, 'sm-detail-row', 'Pattern Seed', String(selected.patternSeed));
    if (selected.isStatTrak && selected.statTrakKills >= 0) {
      appendLabeledRow(detailList, 'sm-detail-row', 'StatTrak Kills', String(selected.statTrakKills));
    }
    appendLabeledRow(detailList, 'sm-detail-row', 'Name Tag', selected.nameTag || 'None');
    appendLabeledRow(detailList, 'sm-detail-row', 'Listed', formatDate(selected.listedAt));
    appendLabeledRow(detailList, 'sm-detail-row', 'Status', selected.status);
    this.detailEl.appendChild(detailList);

    this.appendStickerList(this.detailEl, selected);
    this.appendInsights(this.detailEl, selected);

    this.appendTradeLockBanner(this.detailEl, selected);

    let buyBtn: HTMLElement | null = null;
    let cancelBtn: HTMLElement | null = null;
    if (canPurchase) {
      buyBtn = document.createElement('button');
      buyBtn.className = 'sm-buy-btn';
      buyBtn.setAttribute('data-listing-id', selected.listingId);
      buyBtn.setAttribute('data-action', 'buy');
      buyBtn.textContent = `BUY — ${formatPrice(selected.price)}`;
      this.detailEl.appendChild(buyBtn);
    } else if (canCancel) {
      cancelBtn = document.createElement('button');
      cancelBtn.className = 'sm-cancel-btn';
      cancelBtn.setAttribute('data-listing-id', selected.listingId);
      cancelBtn.setAttribute('data-action', 'cancel-listing');
      cancelBtn.textContent = `CANCEL LISTING — ${formatPrice(selected.price)}`;
      this.detailEl.appendChild(cancelBtn);
    } else {
      appendTextElement(
        this.detailEl,
        'div',
        'sm-subtitle',
        this.sourceMessage
          ? 'Purchasing is unavailable while the live market API is offline.'
          : selected.status !== 'active'
            ? 'This listing is no longer available to purchase.'
            : 'You can\'t buy your own listing.',
      );
    }

    const dynamicOptions = { signal: this.renderController.signal };
    if (buyBtn) {
      buyBtn.addEventListener('click', () => this.openConfirmModal(selected), dynamicOptions);
    }
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => void this.handleCancelListing(selected), dynamicOptions);
    }
  }

  private appendFloatBar(host: HTMLElement, listing: MarketListing, wearRange?: [number, number]): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'sm-float-bar';
    wrapper.setAttribute('data-role', 'float-bar');

    const track = document.createElement('div');
    track.className = 'sm-float-bar-track';
    const needle = document.createElement('div');
    needle.className = 'sm-float-needle';
    const clamped = Math.max(0, Math.min(1, listing.wearFloat));
    needle.style.left = `${(clamped * 100).toFixed(2)}%`;
    needle.setAttribute('data-role', 'float-needle');
    track.appendChild(needle);
    wrapper.appendChild(track);

    const labels = document.createElement('div');
    labels.className = 'sm-float-bar-labels';
    for (const breakpoint of WEAR_LEVEL_BREAKPOINTS) {
      const label = document.createElement('span');
      label.textContent = breakpoint.label;
      label.style.color = breakpoint.color;
      labels.appendChild(label);
    }
    wrapper.appendChild(labels);

    const valueText = document.createElement('div');
    valueText.className = 'sm-float-bar-value';
    valueText.setAttribute('data-role', 'float-bar-value');
    const rangeText = wearRange
      ? ` · skin range ${wearRange[0].toFixed(2)}–${wearRange[1].toFixed(2)}`
      : '';
    valueText.textContent = `Float ${listing.wearFloat.toFixed(4)}${rangeText}`;
    wrapper.appendChild(valueText);

    host.appendChild(wrapper);
  }

  private appendStickerList(host: HTMLElement, listing: MarketListing): void {
    const stickers = (listing.stickers ?? []).filter((s): s is StickerInstance => !!s);
    if (stickers.length === 0) return;

    const heading = document.createElement('div');
    heading.className = 'sm-label';
    heading.textContent = 'Applied Stickers';
    heading.setAttribute('data-role', 'sticker-heading');
    host.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'sm-stickers';
    grid.setAttribute('data-role', 'sticker-list');
    for (const sticker of stickers) {
      const def = STICKER_MANIFEST.get(sticker.stickerId);
      const row = document.createElement('div');
      row.className = 'sm-sticker-row';

      const left = document.createElement('div');
      const name = document.createElement('span');
      name.className = 'sm-sticker-name';
      name.textContent = def?.name ?? `Sticker #${sticker.stickerId}`;
      left.appendChild(name);
      if (def?.holographic) {
        const finish = document.createElement('span');
        finish.className = 'sm-sticker-finish holo';
        finish.textContent = 'Holo';
        left.appendChild(finish);
      }
      if (def?.foil) {
        const finish = document.createElement('span');
        finish.className = 'sm-sticker-finish foil';
        finish.textContent = 'Foil';
        left.appendChild(finish);
      }
      if (def?.glitter) {
        const finish = document.createElement('span');
        finish.className = 'sm-sticker-finish glitter';
        finish.textContent = 'Glitter';
        left.appendChild(finish);
      }
      row.appendChild(left);

      const right = document.createElement('span');
      right.className = 'sm-sticker-meta';
      const wearPct = Math.round(sticker.wear * 100);
      right.textContent = `Slot ${sticker.slot + 1} · ${wearPct}% scraped`;
      row.appendChild(right);

      grid.appendChild(row);
    }
    host.appendChild(grid);
  }

  private appendInsights(host: HTMLElement, listing: MarketListing): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'sm-insights';
    wrapper.setAttribute('data-role', 'market-insights');

    const title = document.createElement('h4');
    title.className = 'sm-insights-title';
    title.textContent = 'Market Insights';
    wrapper.appendChild(title);

    const grid = document.createElement('div');
    grid.className = 'sm-insights-grid';
    grid.setAttribute('data-role', 'insights-grid');
    appendLabeledRow(grid, 'sm-insights-row', 'Loading…', '—');
    wrapper.appendChild(grid);

    const spark = document.createElement('div');
    spark.className = 'sm-spark';
    spark.setAttribute('data-role', 'insights-sparkline');
    wrapper.appendChild(spark);

    host.appendChild(wrapper);

    void this.loadAndRenderStats(listing, grid, spark);
  }

  private async loadAndRenderStats(listing: MarketListing, grid: HTMLElement, spark: HTMLElement): Promise<void> {
    if (this.sourceMessage) {
      // Live API offline — render fallback insights from current listings so
      // the panel never sits as a perpetual "Loading…" stub.
      this.renderInsightsFallback(listing, grid, spark);
      return;
    }
    try {
      const url = new URL('/api/market/stats', `${this.apiBaseUrl}/`);
      url.searchParams.set('skinId', String(listing.skinId));
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`stats unavailable (${response.status})`);
      const stats = await response.json() as Partial<SkinMarketStats>;
      this.renderInsightsFromStats(stats, grid, spark);
    } catch {
      this.renderInsightsFallback(listing, grid, spark);
    }
  }

  private renderInsightsFromStats(stats: Partial<SkinMarketStats>, grid: HTMLElement, spark: HTMLElement): void {
    clearElement(grid);
    appendLabeledRow(grid, 'sm-insights-row', 'Lowest', stats.lowestPrice ? formatPrice(stats.lowestPrice) : '—');
    appendLabeledRow(grid, 'sm-insights-row', 'Median', stats.medianPrice ? formatPrice(stats.medianPrice) : '—');
    appendLabeledRow(grid, 'sm-insights-row', 'Active', String(stats.activeListings ?? 0));
    appendLabeledRow(grid, 'sm-insights-row', '7d Sales', String(stats.recentSales ?? 0));
    this.renderSparkline(spark, stats.priceHistory ?? []);
  }

  private renderInsightsFallback(listing: MarketListing, grid: HTMLElement, spark: HTMLElement): void {
    const sameSkin = this.listings.filter((l) => l.skinId === listing.skinId && l.status === 'active');
    const prices = sameSkin.map((l) => l.price).sort((a, b) => a - b);
    const lowest = prices[0] ?? listing.price;
    const median = prices.length > 0 ? prices[Math.floor(prices.length / 2)] : listing.price;
    clearElement(grid);
    appendLabeledRow(grid, 'sm-insights-row', 'Lowest', formatPrice(lowest));
    appendLabeledRow(grid, 'sm-insights-row', 'Median', formatPrice(median));
    appendLabeledRow(grid, 'sm-insights-row', 'Active', String(sameSkin.length));
    appendLabeledRow(grid, 'sm-insights-row', '7d Sales', '—');

    clearElement(spark);
    const empty = document.createElement('div');
    empty.className = 'sm-spark-empty';
    empty.textContent = 'Sparkline unavailable while market API is offline.';
    spark.appendChild(empty);
  }

  private renderSparkline(host: HTMLElement, history: ReadonlyArray<{ averagePrice?: number }>): void {
    clearElement(host);
    if (!history.length) {
      const empty = document.createElement('div');
      empty.className = 'sm-spark-empty';
      empty.textContent = 'No recent price data.';
      host.appendChild(empty);
      return;
    }
    const values = history
      .map((entry) => Number(entry.averagePrice ?? 0))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (!values.length) {
      const empty = document.createElement('div');
      empty.className = 'sm-spark-empty';
      empty.textContent = 'No recent price data.';
      host.appendChild(empty);
      return;
    }
    const maxValue = Math.max(...values);
    for (const value of values) {
      const bar = document.createElement('div');
      bar.className = 'sm-spark-bar';
      bar.setAttribute('data-role', 'spark-bar');
      const heightPct = maxValue > 0 ? Math.max(8, (value / maxValue) * 100) : 8;
      bar.style.height = `${heightPct.toFixed(1)}%`;
      bar.setAttribute('title', formatPrice(value));
      host.appendChild(bar);
    }
  }

  private appendTradeLockBanner(host: HTMLElement, listing: MarketListing): void {
    const lockUntil = listing.tradeLockUntilMs ?? 0;
    const isLocked = lockUntil > Date.now();
    const banner = document.createElement('div');
    banner.className = isLocked ? 'sm-lock-banner sm-lock-active' : 'sm-lock-banner';
    banner.setAttribute('data-role', 'trade-lock-banner');

    const icon = document.createElement('span');
    icon.className = 'sm-lock-icon';
    icon.textContent = isLocked ? '🔒' : '🛡';
    banner.appendChild(icon);

    const text = document.createElement('span');
    if (isLocked) {
      text.textContent = `Trade-locked until ${formatDate(lockUntil)} — cannot be re-traded after purchase until lock clears.`;
    } else {
      const durationDays = Math.max(1, Math.round((listing.tradeLockDurationMs ?? DEFAULT_TRADE_LOCK_DURATION_MS) / (24 * 60 * 60 * 1000)));
      text.textContent = `${durationDays}-day trade lock applies after purchase.`;
    }
    banner.appendChild(text);

    host.appendChild(banner);
  }

  private refresh(): void {
    this.filters.offset = 0;
    void this.loadListings(true);
  }

  private async renderListingPreview(listing: MarketListing, host: HTMLElement): Promise<void> {
    if (!this.previewRenderer || !this.skinLoader) return;
    const requestToken = ++this.previewRequestToken;

    try {
      const canvas = await this.previewRenderer.renderPreview(
        listing.weaponId,
        listing.skinId,
        listing.wearFloat,
        this.skinLoader,
        {
          stickers: listing.stickers,
          nameTag: listing.nameTag,
        },
      );
      if (requestToken !== this.previewRequestToken) return;
      clearElement(host);
      host.appendChild(canvas);
    } catch {
      if (requestToken !== this.previewRequestToken) return;
      host.textContent = 'Preview unavailable';
    }
  }

  private async handleCreateListing(): Promise<void> {
    const skinId = Number(this.sellSkinSelect?.value ?? 0);
    const price = Number(this.sellPriceInput?.value ?? 0);
    const skin = weaponSkinRegistry.getSkin(skinId);
    if (!skin || !Number.isFinite(price) || price < 1) {
      this.errorMessage = 'Choose a skin and enter a valid price before listing.';
      this.render();
      return;
    }

    const wearFloat = Number(seededNumber(skin.id * 61, skin.wearRange[0], skin.wearRange[1]).toFixed(3));
    const isStatTrak = !!this.sellStatTrakInput?.checked && skin.statTrakEligible;
    const nameTag = (this.sellNameTagInput?.value ?? '').trim().slice(0, 20);
    const payload: CreateListingRequest = {
      instanceId: `local-${skin.id}-${Date.now()}`,
      skinId: skin.id,
      skinName: skin.name,
      weaponId: skin.weaponIds[0] ?? 'unknown',
      rarity: skin.rarity,
      wearFloat,
      patternSeed: skin.pattern ? seededInteger(skin.id * 67, skin.pattern.seedRange[0], skin.pattern.seedRange[1]) : 0,
      isStatTrak,
      statTrakKills: isStatTrak ? seededInteger(skin.id * 71, 0, 120) : -1,
      nameTag,
      stickers: buildListingStickers(skin.id),
      price: Math.round(price),
    };

    try {
      const resp = await fetch('/api/market/list', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({})) as { error?: string; listing?: MarketListing };
      if (!resp.ok) {
        // BUG-W-18: 401 means the JWT is missing/expired — prompt re-auth.
        if (resp.status === 401) {
          this.errorMessage = 'Please log in to purchase.';
        } else {
          this.errorMessage = data.error ?? 'Listing failed';
        }
        this.render();
        return;
      }
      this.errorMessage = '';
      if (data.listing?.listingId) {
        this.selectedListingId = data.listing.listingId;
      }
      this.refresh();
    } catch {
      this.errorMessage = 'Network error — listing failed';
      this.render();
    }
  }

  private openConfirmModal(listing: MarketListing): void {
    this.confirmingListing = listing;
    this.confirmInFlight = false;
    this.confirmError = '';
    this.renderConfirmModal();
  }

  private closeConfirmModal(): void {
    this.confirmingListing = null;
    this.confirmInFlight = false;
    this.confirmError = '';
    this.renderConfirmModal();
  }

  private renderConfirmModal(): void {
    if (!this.confirmOverlay || !this.confirmModal) return;
    if (!this.confirmingListing) {
      this.confirmOverlay.classList.remove('sm-confirm-visible');
      this.confirmOverlay.setAttribute('aria-hidden', 'true');
      clearElement(this.confirmModal);
      return;
    }
    const listing = this.confirmingListing;
    this.confirmOverlay.classList.add('sm-confirm-visible');
    this.confirmOverlay.setAttribute('aria-hidden', 'false');
    clearElement(this.confirmModal);

    const title = document.createElement('h3');
    title.className = 'sm-confirm-title';
    title.textContent = `Confirm purchase`;
    this.confirmModal.appendChild(title);

    appendTextElement(this.confirmModal, 'div', 'sm-card-title', listing.skinName, rarityColor(listing.rarity));
    appendChipRow(this.confirmModal, [
      listing.weaponId,
      wearLevelDisplayName(wearLevelFromFloat(listing.wearFloat)),
      ...(listing.isStatTrak ? ['StatTrak'] : []),
    ]);

    const summary = document.createElement('div');
    summary.className = 'sm-confirm-summary';
    const fee = calculateMarketFee(listing.price);
    const sellerProceeds = calculateSellerProceeds(listing.price);
    appendLabeledRow(summary, 'sm-confirm-row', 'List price', formatPrice(listing.price));
    appendLabeledRow(summary, 'sm-confirm-row', 'Market fee', formatPrice(fee));
    appendLabeledRow(summary, 'sm-confirm-row', 'Seller receives', formatPrice(sellerProceeds));
    appendLabeledRow(summary, 'sm-confirm-row sm-total', 'You pay', formatPrice(listing.price));
    // BUG-W-17: surface the player's current balance so they don't click
    // Confirm only to get a 4xx insufficient-funds rejection from the server.
    const balance = this.getPlayerBalance();
    if (balance !== null) {
      appendLabeledRow(summary, 'sm-confirm-row', 'Your balance', formatPrice(balance));
    }
    this.confirmModal.appendChild(summary);

    const insufficientFunds = balance !== null && balance < listing.price;
    if (insufficientFunds) {
      const shortfall = document.createElement('div');
      shortfall.className = 'sm-confirm-warning';
      shortfall.setAttribute('data-role', 'confirm-insufficient-funds');
      shortfall.textContent = `Insufficient funds — need ${formatPrice(listing.price - (balance ?? 0))} more.`;
      this.confirmModal.appendChild(shortfall);
    }

    const durationDays = Math.max(1, Math.round((listing.tradeLockDurationMs ?? DEFAULT_TRADE_LOCK_DURATION_MS) / (24 * 60 * 60 * 1000)));
    const warning = document.createElement('div');
    warning.className = 'sm-confirm-warning';
    warning.setAttribute('data-role', 'confirm-trade-lock');
    warning.textContent = `🔒 ${durationDays}-day trade lock — this item can't be retraded for ${durationDays} days after purchase.`;
    this.confirmModal.appendChild(warning);

    if (this.confirmError) {
      const err = document.createElement('div');
      err.className = 'sm-confirm-error';
      err.textContent = this.confirmError;
      this.confirmModal.appendChild(err);
    }

    const actions = document.createElement('div');
    actions.className = 'sm-confirm-actions';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'sm-confirm-btn';
    cancel.setAttribute('data-action', 'confirm-cancel');
    cancel.textContent = 'Cancel';
    cancel.disabled = this.confirmInFlight;
    const modalOptions = { signal: this.renderController.signal };
    cancel.addEventListener('click', () => this.closeConfirmModal(), modalOptions);
    actions.appendChild(cancel);

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'sm-confirm-btn sm-confirm-primary';
    confirm.setAttribute('data-action', 'confirm-buy');
    if (this.confirmInFlight) {
      confirm.textContent = 'Confirming…';
    } else if (insufficientFunds) {
      confirm.textContent = 'Insufficient funds';
    } else {
      confirm.textContent = `Confirm — ${formatPrice(listing.price)}`;
    }
    // BUG-W-17: disable Confirm when we know the balance is below the price
    // so users can't fire a guaranteed-failing buy request.
    confirm.disabled = this.confirmInFlight || insufficientFunds;
    confirm.addEventListener('click', () => void this.confirmPurchase(), modalOptions);
    actions.appendChild(confirm);

    this.confirmModal.appendChild(actions);
  }

  private async confirmPurchase(): Promise<void> {
    if (!this.confirmingListing || this.confirmInFlight) return;
    const listing = this.confirmingListing;
    this.confirmInFlight = true;
    this.confirmError = '';
    this.renderConfirmModal();
    try {
      const resp = await fetch('/api/market/buy', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ listingId: listing.listingId }),
      });
      const body = await resp.json().catch(() => ({})) as Partial<PurchaseResult> & { error?: string };
      if (!resp.ok || !body.success) {
        // BUG-W-18: 401 means the JWT is missing/expired — prompt re-auth
        // instead of showing the raw status code.
        if (resp.status === 401) {
          this.confirmError = 'Please log in to purchase.';
        } else {
          this.confirmError = body.error ?? `Purchase failed (${resp.status})`;
        }
        this.confirmInFlight = false;
        this.renderConfirmModal();
        return;
      }
      const lockSuffix = body.tradeLockUntilMs
        ? ` Trade-locked until ${formatDate(body.tradeLockUntilMs)}.`
        : '';
      this.showToast(`Purchased ${listing.skinName} for ${formatPrice(listing.price)}.${lockSuffix}`);
      this.closeConfirmModal();
      this.refresh();
    } catch {
      this.confirmError = 'Network error — purchase failed';
      this.confirmInFlight = false;
      this.renderConfirmModal();
    }
  }

  private async handleCancelListing(listing: MarketListing): Promise<void> {
    try {
      const resp = await fetch('/api/market/cancel', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ listingId: listing.listingId }),
      });
      const body = await resp.json().catch(() => ({})) as Partial<CancelListingResult> & { error?: string };
      if (!resp.ok || !body.success) {
        // BUG-W-18: 401 means the JWT is missing/expired — prompt re-auth.
        if (resp.status === 401) {
          this.showToast('Please log in to purchase.', 'error');
        } else {
          this.showToast(body.error ?? `Cancel failed (${resp.status})`, 'error');
        }
        return;
      }
      this.showToast(`Listing for ${listing.skinName} cancelled.`);
      this.refresh();
    } catch {
      this.showToast('Network error — cancel failed', 'error');
    }
  }

  private showToast(message: string, kind: 'success' | 'error' = 'success'): void {
    if (!this.toastEl) return;
    if (this.toastTimer !== null) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
    this.toastEl.textContent = message;
    this.toastEl.classList.remove('sm-toast-error');
    if (kind === 'error') this.toastEl.classList.add('sm-toast-error');
    this.toastEl.classList.add('sm-toast-visible');
    const timeoutFn = typeof setTimeout === 'function' ? setTimeout : null;
    if (timeoutFn) {
      this.toastTimer = timeoutFn(() => {
        this.toastEl?.classList.remove('sm-toast-visible');
        this.toastTimer = null;
      }, 3500) as unknown as number;
    }
  }

  private renderFooter(): void {
    const currentOffset = this.filters.offset ?? 0;
    if (this.summaryEl) {
      const end = Math.min(this.totalCount, currentOffset + this.listings.length);
      this.summaryEl.textContent = this.totalCount > 0
        ? `Listings ${currentOffset + 1}-${end} of ${this.totalCount}`
        : '0 listings';
    }
    if (this.pagePrevBtn) this.pagePrevBtn.disabled = currentOffset <= 0 || this.loading;
    if (this.pageNextBtn) this.pageNextBtn.disabled = this.loading || currentOffset + this.listings.length >= this.totalCount;
  }
}
